import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StudentScholarship,
  StudentScholarshipEditResource,
  StudentScholarshipViewResource,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentScholarshipPageBody } from "./StudentScholarshipPageBody";
import { StudentScholarshipPageHeader } from "./StudentScholarshipPageHeader";

const routeName = "studentScholarship";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
}

export const studentScholarshipPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Scholarship",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentScholarshipPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentScholarship;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [scholarship, setScholarship] = useState<StudentScholarship>({} as StudentScholarship);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [scholarship] });
  const [viewResource, setViewResource] = useState<StudentScholarshipViewResource>({});
  const [editResource, setEditResource] = useState<StudentScholarshipEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${studentScholarshipPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`
    );

  const fetch = (id: number) => {
    ApiService.studentScholarships.get(id).then((response) => {
      if (id === 0) {
        if (optionalParams.studentId) response.studentId = optionalParams.studentId;
      }

      setScholarship(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentScholarships.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentScholarships.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentScholarshipPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentScholarshipPageRoute.handle}
      crudMode={params.crudMode}
      fetch={fetch}
      reroute={reroute}
      scholarship={scholarship}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentScholarshipPageBody
      crudMode={params.crudMode}
      scholarship={scholarship}
      setScholarship={setScholarship}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
